import React from 'react';

import {IconRefresh} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';

import {
  ContentContainer, TypographyBody2, TypographyH5,
} from './Style.js';

import MagnifyIllustration from 'assets/svg/magnifying-glass-illustration.svg';
import RoadRepair from 'assets/svg/road-repair-illustration.svg';
import {ZmTertiaryBtn} from 'Components/ZmButtons/index.js';
import {
  ErrorExtEvent404,
  ErrorMsgBody400LinkUsed,
  ErrorMsgBody404,
  ErrorMsgBody500,
  ErrorMsgBody500BadCalendar,
} from 'Error/ErrorMsgConst.js';


/**
 * helper function to map header to a reason
 * @param {String} reason
 * @param {String} displayName
 * @return {String}
 */
function getHeader(reason, displayName) {
  let header = 'error.errorDefault';
  switch (reason) {
    case ErrorMsgBody404:
      header = 'error.errorMsgHeader404';
      break;
    case ErrorMsgBody500:
      header = 'error.errorMsgHeader500';
      break;
    case ErrorMsgBody500BadCalendar:
      if (displayName) {
        header = 'error.errorMsgHeader500BadCalendarWithHost';
      } else {
        header = 'error.errorMsgHeader500BadCalendar';
      }
      break;
    case ErrorExtEvent404:
      header = 'error.errorMsgHeaderExtEvent404';
      break;
    case ErrorMsgBody400LinkUsed:
      header = 'booking.fetchErrorLinkUsedHead';
      break;
    default:
      break;
  }
  return header;
}

/**
 * helper function to map explanation text to a reason
 * @param {string} reason
 * @param {string} displayName
 * @return {string}
 */
function getExplanationText(reason, displayName) {
  let explanation = reason;
  switch (reason) {
    case ErrorMsgBody404:
      explanation = 'error.errorMsgBody404';
      break;
    case ErrorMsgBody500:
      explanation = 'error.errorMsgBody500';
      break;
    case ErrorMsgBody500BadCalendar:
      if (displayName) {
        explanation = 'error.errorMsgBody500BadCalendarWithHost';
      } else {
        explanation = 'error.errorMsgBody500BadCalendar';
      }
      break;
    case ErrorExtEvent404:
      explanation = 'error.errorMsgBodyExtEvent404';
      break;
    default:
      break;
  }
  return explanation;
}

/**
 * Error Display Component
 * @param {Object} props
 * @param {string} [props.displayName]
 * @param {{reason: string}} props.errorInfo
 * @param {function} [props.onReset]
 * @return {ReactElement}
 */
export default function ErrorDisplay(props) {
  const {
    displayName,
    errorInfo,
    onReset,
  } = props;
  const {t} = useTranslation();

  const display500 = [
    ErrorMsgBody500, ErrorMsgBody500BadCalendar,
  ].includes(errorInfo.reason);

  return (
    <ContentContainer>
      {
        display500 ?
        <img height="120" width="120" src={RoadRepair} alt="road-repair"/>:
        <img height="120" width="120" src={MagnifyIllustration} alt="magnifying-glass"/>
      }
      <TypographyH5>
        {
          displayName ?
            t(getHeader(t(errorInfo.reason), displayName), {host: displayName}) :
            t(getHeader(t(errorInfo.reason)))
        }
      </TypographyH5>
      <TypographyBody2>
        {
          displayName ?
            t(getExplanationText(errorInfo.reason, displayName), {host: displayName}) :
            t(getExplanationText(errorInfo.reason))
        }
      </TypographyBody2>
      {onReset &&
        <ZmTertiaryBtn
          size="small"
          onClick={onReset}
          startIcon={<IconRefresh />}
        >
          {t('common.refresh')}
        </ZmTertiaryBtn>
      }
    </ContentContainer>
  );
}
